import AppLinks from 'assets/applinks.routes';
import LocalStorageWrapper, { LocalStorageKeys } from 'common/utils/storage-utils';
import React, {
	createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { UserApiImpl } from '../../data/remote/user-api-impl';
import { ApiResponse } from 'common/domain/entities/api-response';
import { SimpleUserBackofficeRolesModel } from '../../data/dto/user-backoffice-model';

type TContextProps = {
    loading: boolean;
    error: string;
	setError: React.Dispatch<React.SetStateAction<string>>;
    login: (username: string, password: string) => Promise<boolean | {
		requireTwoFactor: boolean, userId: number,
	}>;
	verifyTwoFactorCode: (userId: string, code: string) => Promise<boolean>;
	forgotPassword: (username: string) => Promise<boolean | string>;
    loggedUser: SimpleUserBackofficeRolesModel | null;
	setUser: (params: SimpleUserBackofficeRolesModel | null) => void;
	resetPassword: (email: string, password: string, token: string) => Promise<boolean | string>;
}
const AuthContext = createContext({} as TContextProps);

export const AuthBackofficeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const navigate = useNavigate();
	const authApi = new UserApiImpl();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [loggedUser, setLoggedUser] = useState<SimpleUserBackofficeRolesModel | null >(null);

	useEffect(() => {
		const key = LocalStorageWrapper.get<string>(LocalStorageKeys.REFRESH_TOKEN);
		if (window.location.href.indexOf(AppLinks.RESET_PASSWORD_BACKOFFICE) > -1) {
			return;
		}
		if (!key && (window.location.href.indexOf(AppLinks.DASHBOARD) > -1)) {
			navigate(AppLinks.LOGIN_BACKOFFICE);
			return;
		}
		const user = LocalStorageWrapper.get<SimpleUserBackofficeRolesModel>(LocalStorageKeys.USER);
		setLoggedUser(user);
	}, []);

	const login = async (username: string, password: string) => {
		setLoading(true);
		setError('');
		return authApi.loginBackofficeUser(username, password).then((result) => {
			if (result.data.requiresTwoFactor) {
				return { requireTwoFactor: true, userId: result.data.userId };
			}
			const { authToken: newAuthToken } = result.data;
			LocalStorageWrapper.set(LocalStorageKeys.AUTH_TOKEN, newAuthToken);
			LocalStorageWrapper.set(LocalStorageKeys.REFRESH_TOKEN, result.data.refreshToken);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const user: any = result.data;
			delete user.authToken;
			delete user.refreshToken;
			LocalStorageWrapper.set(LocalStorageKeys.USER, user);
			setLoggedUser(user);
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setError(responseError.message);
			return false;
		}).finally(() => {
			setLoading(false);
		});
	};

	const verifyTwoFactorCode = (userId: string, code: string) => {
		setLoading(true);
		setError('');
		return authApi.verifyTwoFactor(userId, code).then((result) => {
			const { authToken: newAuthToken } = result.data;
			LocalStorageWrapper.set(LocalStorageKeys.AUTH_TOKEN, newAuthToken);
			LocalStorageWrapper.set(LocalStorageKeys.REFRESH_TOKEN, result.data.refreshToken);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const user: any = result.data;
			delete user.authToken;
			delete user.refreshToken;
			LocalStorageWrapper.set(LocalStorageKeys.USER, user);
			setLoggedUser(user);
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setError(responseError.message);
			return false;
		}).finally(() => {
			setLoading(false);
		});
	};

	const forgotPassword = async (username: string) => {
		setLoading(true);
		setError('');
		return authApi.forgotBackofficePassword(username).then((resp) => resp.message).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setError(responseError.message);
			return false;
		}).finally(() => {
			setLoading(false);
		});
	};

	const resetPassword = async (email: string, password: string, token: string) => {
		setLoading(true);
		setError('');
		return authApi.resetPassword(email, token, password).then((resp) => (
			resp.message
		)).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setError(responseError.message);
			return false;
		}).finally(() => {
			setLoading(false);
		});
	};

	const setUser = (user: SimpleUserBackofficeRolesModel | null) => {
		setLoggedUser(user);
		LocalStorageWrapper.set(LocalStorageKeys.USER, user);
	};

	const value = useMemo(() => ({
		login,
		loading,
		error,
		loggedUser,
		setUser,
		forgotPassword,
		setError,
		resetPassword,
		verifyTwoFactorCode,
	}), [loading, error, loggedUser]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default function useAuthBackoffice() {
	return useContext(AuthContext);
}
